// CU-86d45b38j — the Stripe.js card form extracted from AddPaymentMethodModal; it hands the opaque payment method id to
// whichever attach contract the host chooses and knows nothing about who owns the customer.
import React, { useState } from "react";
import { Form, Input, Button, Space, Alert } from "antd";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { getStripe } from "../../utils/stripe/stripeConfig";
import { useNotifications } from "../../context/NotificationContext";

export interface PaymentMethodCardFormProps {
	onAttach: (paymentMethodId: string) => Promise<unknown>;
	onSuccess: () => void;
	onCancel: () => void;
}

const CARD_ELEMENT_OPTIONS = {
	style: {
		base: {
			fontSize: "16px",
			color: "#424770",
			"::placeholder": {
				color: "#aab7c4",
			},
			iconColor: "#666EE8",
		},
		invalid: {
			color: "#fa755a",
			iconColor: "#fa755a",
		},
	},
	hidePostalCode: true,
};

const CardForm: React.FC<PaymentMethodCardFormProps> = ({ onAttach, onSuccess, onCancel }) => {
	const stripe = useStripe();
	const elements = useElements();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { showSuccess, showError } = useNotifications();

	const handleSubmit = async (values: any) => {
		if (!stripe || !elements) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const cardElement = elements.getElement(CardElement);

			if (!cardElement) {
				throw new Error("Card element not found");
			}

			const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
				type: "card",
				card: cardElement,
				billing_details: {
					name: values.name,
					email: values.email,
					address: {
						line1: values.address,
						city: values.city,
						state: values.state,
						postal_code: values.postalCode,
						country: values.country || "AU",
					},
				},
			});

			if (stripeError) {
				throw new Error(stripeError.message);
			}

			if (!paymentMethod) {
				throw new Error("Failed to create payment method");
			}

			await onAttach(paymentMethod.id);

			showSuccess("Payment method added successfully");
			onSuccess();
		} catch (err: any) {
			console.error("Error adding payment method:", err);
			setError(err.message || "Failed to add payment method");
			showError(err.message || "Failed to add payment method");
		} finally {
			setLoading(false);
		}
	};

	return (
		<Form form={form} layout="vertical" onFinish={handleSubmit}>
			{error && <Alert message="Error" description={error} type="error" closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

			<Form.Item label="Cardholder Name" name="name" rules={[{ required: true, message: "Please enter cardholder name" }]}>
				<Input placeholder="John Smith" />
			</Form.Item>

			<Form.Item
				label="Email"
				name="email"
				rules={[
					{ required: true, message: "Please enter email" },
					{ type: "email", message: "Please enter valid email" },
				]}
			>
				<Input placeholder="john@example.com" />
			</Form.Item>

			<Form.Item label="Card Details" required>
				<div
					style={{
						padding: "12px",
						border: "1px solid #d9d9d9",
						borderRadius: "6px",
						background: "#fff",
					}}
				>
					<CardElement options={CARD_ELEMENT_OPTIONS} />
				</div>
			</Form.Item>

			<Form.Item label="Address" name="address">
				<Input placeholder="123 Main St" />
			</Form.Item>

			<Space style={{ width: "100%" }} size="middle">
				<Form.Item label="City" name="city" style={{ flex: 1, marginBottom: 0 }}>
					<Input placeholder="Melbourne" />
				</Form.Item>

				<Form.Item label="State" name="state" style={{ flex: 1, marginBottom: 0 }}>
					<Input placeholder="VIC" />
				</Form.Item>

				<Form.Item label="Postal Code" name="postalCode" style={{ flex: 1, marginBottom: 0 }}>
					<Input placeholder="3000" />
				</Form.Item>
			</Space>

			<Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
				<Space style={{ width: "100%", justifyContent: "flex-end" }}>
					<Button onClick={onCancel} disabled={loading}>
						Cancel
					</Button>
					<Button type="primary" htmlType="submit" loading={loading} disabled={!stripe}>
						Add Payment Method
					</Button>
				</Space>
			</Form.Item>
		</Form>
	);
};

const PaymentMethodCardForm: React.FC<PaymentMethodCardFormProps> = (props) => {
	const stripePromise = getStripe();

	return (
		<Elements stripe={stripePromise}>
			<CardForm {...props} />
		</Elements>
	);
};

export default PaymentMethodCardForm;
